import React, { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, ImagePlus, Search, Trash2, Upload } from "lucide-react";
import { catalogTaxonomy, catalogTaxonomyImage, saveCatalogTaxonomyImage } from "../api/catalogSearchApi";
import styles from "./CatalogCategoryPhotosPage.module.css";

function filterTree(nodes, text) {
    if (!text.trim()) return nodes || [];
    const query = text.trim().toLowerCase();
    return (nodes || []).flatMap(node => {
        const children = filterTree(node.children || [], text);
        return (node.label || "").toLowerCase().includes(query) || (node.code || "").toLowerCase().includes(query) || children.length
            ? [{ ...node, children }] : [];
    });
}

async function encodePhoto(file) {
    if (!["image/jpeg", "image/png", "image/webp"].includes(file?.type)) throw new Error("Выберите JPEG, PNG или WebP.");
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, 800 / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext("2d").drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close?.();
    const makeBlob = quality => new Promise(resolve => canvas.toBlob(resolve, "image/jpeg", quality));
    let quality = 0.86;
    let blob = await makeBlob(quality);
    while (blob && blob.size > 250000 && quality > 0.4) {
        quality -= 0.12;
        blob = await makeBlob(quality);
    }
    if (!blob || blob.size > 300000) throw new Error("Фото не удалось сжать до 300 КБ.");
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error("Не удалось прочитать фото."));
        reader.readAsDataURL(blob);
    });
}

function PhotoNode({ node, depth, filtering, busyId, onSave, onRemove }) {
    const [open, setOpen] = useState(false);
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState("");
    const [storedPreview, setStoredPreview] = useState("");
    const busy = busyId === node.id;
    useEffect(() => {
        let active = true;
        let objectUrl = "";
        if (!node.image_url) { setStoredPreview(""); return undefined; }
        catalogTaxonomyImage(node.image_url).then(url => {
            objectUrl = url;
            if (active) setStoredPreview(url); else URL.revokeObjectURL(url);
        }).catch(() => { if (active) setStoredPreview(""); });
        return () => { active = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
    }, [node.image_url]);
    useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);
    const choose = event => {
        const next = event.target.files?.[0] || null;
        setFile(next);
        setPreview(next ? URL.createObjectURL(next) : "");
        event.target.value = "";
    };
    const save = async () => {
        if (!file) return;
        try { await onSave(node, await encodePhoto(file)); setFile(null); setPreview(""); } catch { /* parent shows the error */ }
    };
    return <div className={styles.node}>
        <div className={styles.row} style={{ "--depth": depth }}>
            <button className={styles.disclosure} type="button" disabled={!node.children?.length}
                aria-label={(open || filtering ? "Свернуть " : "Развернуть ") + node.label} onClick={() => setOpen(value => !value)}>
                {node.children?.length ? (open || filtering ? <ChevronDown size={15} /> : <ChevronRight size={15} />) : null}
            </button>
            <span className={styles.label}>{node.label}<small>{node.count ?? 0} позиций</small></span>
            <div className={styles.preview}>
                {(preview || storedPreview) ? <img src={preview || storedPreview} alt={"Фото: " + node.label} /> : <span><ImagePlus size={18} />Без своего фото</span>}
            </div>
            <label className={styles.upload}><Upload size={15} />Загрузить<input type="file" accept="image/jpeg,image/png,image/webp" onChange={choose} /></label>
            <button className={styles.save} type="button" disabled={!file || busy} onClick={save}>{busy ? "Сохраняю…" : "Сохранить"}</button>
            {node.image_url && <button className={styles.remove} type="button" disabled={busy} aria-label={"Удалить фото " + node.label} title="Удалить фото" onClick={() => onRemove(node)}><Trash2 size={16} /></button>}
        </div>
        {(open || filtering) && node.children?.map(child => <PhotoNode key={child.id} node={child} depth={depth + 1} filtering={filtering} busyId={busyId} onSave={onSave} onRemove={onRemove} />)}
    </div>;
}

export default function CatalogCategoryPhotosPage() {
    const [tree, setTree] = useState([]);
    const [filter, setFilter] = useState("");
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");
    const visibleTree = useMemo(() => filterTree(tree, filter), [tree, filter]);
    const load = async () => {
        setLoading(true);
        try { setTree((await catalogTaxonomy()).tree || []); setError(""); }
        catch (e) { setError(e.message || "Не удалось загрузить дерево категорий"); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);
    const save = async (node, data) => {
        setBusyId(node.id); setError(""); setNotice("");
        try { await saveCatalogTaxonomyImage(node.id, data); await load(); setNotice("Фото категории «" + node.label + "» сохранено."); }
        catch (e) { setError(e.message || "Не удалось сохранить фото"); throw e; }
        finally { setBusyId(null); }
    };
    const remove = async node => {
        setBusyId(node.id); setError(""); setNotice("");
        try { await saveCatalogTaxonomyImage(node.id, null); await load(); setNotice("Фото категории «" + node.label + "» удалено."); }
        catch (e) { setError(e.message || "Не удалось удалить фото"); }
        finally { setBusyId(null); }
    };
    return <div className={styles.page}>
        <header className={styles.header}>
            <div><h2>Фотографии категорий</h2><p>Загружайте и заменяйте фото, которые увидят все в каталоге.</p></div>
            <label className={styles.search}><Search size={16} /><input value={filter} onChange={event => setFilter(event.target.value)} placeholder="Найти категорию" /></label>
        </header>
        <p className={styles.hint}>JPEG, PNG или WebP. Фото автоматически сжимается до JPEG размером до 300 КБ. Без своего фото остаётся тематическое изображение по умолчанию.</p>
        {loading && tree.length > 0 && <p className={styles.hint} role="status">Обновляю категории…</p>}
        {error && <p className={styles.error} role="alert">{error}</p>}
        {notice && <p className={styles.notice} role="status">{notice}</p>}
        <section className={styles.tree} aria-label="Категории для настройки фото">
            {loading && !tree.length ? <p>Загрузка категорий…</p> : visibleTree.length
                ? visibleTree.map(node => <PhotoNode key={node.id} node={node} depth={0} filtering={Boolean(filter.trim())} busyId={busyId} onSave={save} onRemove={remove} />)
                : <p>Категории не найдены.</p>}
        </section>
    </div>;
}
